import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import { apiService } from '../services/api';

const AcademicQuestionnaire: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [institution, setInstitution] = useState('');
  const [role, setRole] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [patientsPerMonth, setPatientsPerMonth] = useState('');
  const [isPrincipalInvestigator, setIsPrincipalInvestigator] = useState('');
  const [goals, setGoals] = useState('');

  useEffect(() => {
    const loadUser = async () => {
      try {
        const user = await apiService.getCurrentUser();
        if (!user?.is_academic) {
          navigate('/newsfeed', { replace: true });
          return;
        }
        if (user.academic_questionnaire_completed) {
          navigate('/newsfeed', { replace: true });
          return;
        }
      } catch (err) {
        console.error('Failed to load user for academic questionnaire:', err);
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, [navigate]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        institution: institution.trim(),
        role: role.trim(),
        specialty: specialty.trim(),
        patients_per_month: patientsPerMonth.trim(),
        principal_investigator: isPrincipalInvestigator,
        goals: goals.trim(),
      };
      const result = await apiService.submitAcademicQuestionnaire(payload);
      if (!result?.success) {
        throw new Error(result?.error || 'Failed to save questionnaire.');
      }
      window.location.replace('/');
    } catch (err: any) {
      setError(err?.message || 'Failed to submit questionnaire.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-slate-900 pb-20 relative bg-slate-50 font-sans">
      <Header />
      <div className="max-w-[860px] mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
        <div className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-8">
          <h1 className="text-2xl font-bold text-slate-900">Academic onboarding</h1>
          <p className="text-sm text-slate-500 mt-2">
            Tell us a bit about your academic focus so we can tailor your experience.
          </p>
        </div>

        <div className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <label className="space-y-2 text-sm text-slate-600 block">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Institution</span>
              <input
                value={institution}
                onChange={(e) => setInstitution(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                placeholder="Example: Memorial Sloan Kettering"
                required
              />
            </label>

            <label className="space-y-2 text-sm text-slate-600 block">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Role</span>
              <input
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                placeholder="Example: Medical Oncologist"
                required
              />
            </label>

            <label className="space-y-2 text-sm text-slate-600 block">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Specialty / focus</span>
              <input
                value={specialty}
                onChange={(e) => setSpecialty(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                placeholder="Example: Lung cancer, immunotherapy"
              />
            </label>

            <label className="space-y-2 text-sm text-slate-600 block">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Average number of patients per month
              </span>
              <input
                type="number"
                min={0}
                value={patientsPerMonth}
                onChange={(e) => setPatientsPerMonth(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                placeholder="Example: 25"
              />
            </label>

            <label className="space-y-2 text-sm text-slate-600 block">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Principal investigator on clinical trials?
              </span>
              <select
                value={isPrincipalInvestigator}
                onChange={(e) => setIsPrincipalInvestigator(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white"
              >
                <option value="">Select one</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </label>

            <label className="space-y-2 text-sm text-slate-600 block">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">What are you hoping to do with Ruby?</span>
              <textarea
                value={goals}
                onChange={(e) => setGoals(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm min-h-[120px]"
                placeholder="Share the questions or insights you want to explore."
              />
            </label>

            {error && (
              <div className="rounded-lg border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center justify-center px-4 py-2 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60"
              >
                {submitting ? 'Submitting…' : 'Continue'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AcademicQuestionnaire;
